import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import Table from 'components/generics/Table/ReactTable';
import Button from 'components/generics/Button/Button';
import Modal from 'components/generics/Modal/Modal';
import ConfirmDelete from 'components/generics/ConfirmDelete/ConfirmDelete';
import CourseTitle from 'components/generics/CourseTitle/CourseTitle';
import FormChecklistModel from 'components/pages/parameters/ChecklistModels/FormChecklistModel';
import PlaneTakeOffIcon from 'components/icons/PlaneTakeOffIcon';
import DeleteIcon from 'components/icons/DeleteIcon';
import useAuth from 'providers/Auth/useAuth';

import {
  applyTemplateToActiveFlights,
  deleteTemplate,
  generate,
  getTemplates,
} from 'services/API/Checklist';

import { isAuthorised, prepareTable } from 'utils/func';
import { CHECKLIST_MODEL_HEADERS } from 'utils/const';

import 'components/generics/Checklist/Checklist.style.scss';

const errorMessage = err => err?.response?.data?.message || err?.message || 'Unexpected error';

/**
 * Modèles de checklist : liste, création / édition, suppression et bouton
 * « Générer / Mettre à jour les checklists » (Admin / CPSAdmin).
 */
const ChecklistModels = () => {
  const { user } = useAuth();
  const canManage = isAuthorised(['Admin', 'CPSAdmin'], user?.roleName);

  const [modalVisibility, setModalVisibility] = useState(false);
  const [formType, setFormtype] = useState(null);
  const [editId, setEditId] = useState(null);
  const [selectedModels, setSelectedModels] = useState([]);
  const [data, setData] = useState({ datas: [], headers: [] });
  const [confirmDeletePopup, setConfirmDeletePopup] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [applyCandidate, setApplyCandidate] = useState(null);
  const [applying, setApplying] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    getDatas();
  }, []);

  const getDatas = () => {
    getTemplates()
      .then(res => {
        setData({ datas: prepareTable(CHECKLIST_MODEL_HEADERS, res.data || []), headers: CHECKLIST_MODEL_HEADERS });
      })
      .catch(e => toast.error(errorMessage(e)));
  };

  const openModalEdit = row => {
    setFormtype('edit');
    setEditId(row.id);
    setModalVisibility(true);
  };

  const openModalAdd = () => {
    setFormtype('add');
    setEditId(null);
    setModalVisibility(true);
  };

  const onSubmitForm = () => {
    setModalVisibility(false);
    getDatas();
  };

  const onDelete = () => {
    if (selectedModels.length === 0) return;
    Promise.all(selectedModels.map(model => deleteTemplate(model.original.id)))
      .then(() => {
        setSelectedModels([]);
        setConfirmDeletePopup(false);
        getDatas();
        toast.success('Checklist model(s) deleted');
      })
      .catch(err => toast.error(errorMessage(err)));
  };

  const onDeleteOne = () => {
    if (!deleteCandidate) return;
    deleteTemplate(deleteCandidate.id)
      .then(() => {
        toast.success(`Checklist model “${deleteCandidate.name}” deleted`);
        setDeleteCandidate(null);
        setSelectedModels([]);
        getDatas();
      })
      .catch(err => toast.error(errorMessage(err)));
  };

  const onApplyToActiveFlights = () => {
    if (!applyCandidate) return;
    setApplying(true);
    applyTemplateToActiveFlights(applyCandidate.id)
      .then(res => {
        const result = res.data || {};
        toast.success(
          `“${result.templateName || applyCandidate.name}” added to ${result.added || 0} flight(s) on ${
            result.flights || 0
          } active · run “Generate / Update checklists” to create the checklists`,
        );
        setApplyCandidate(null);
      })
      .catch(err => toast.error(errorMessage(err)))
      .finally(() => setApplying(false));
  };

  const onGenerate = () => {
    setGenerating(true);
    generate()
      .then(res => {
        const result = res.data || {};
        // `deleted` inclut les suppressions liées aux vols non « Validated » ;
        // on les détaille quand il y en a.
        const notValidated = result.deletedNotValidatedFlight || 0;
        toast.success(
          `${result.created || 0} created · ${result.deleted || 0} deleted · ${
            result.skippedFilled || 0
          } filled kept · ${result.keptOrphaned || 0} orphaned kept${
            notValidated > 0 ? ` · ${notValidated} from non-validated flights` : ''
          }`,
        );
        getDatas();
      })
      .catch(err => toast.error(errorMessage(err)))
      .finally(() => setGenerating(false));
  };

  return (
    <div className="params crud" id="checklist-models">
      <div className="crud__header">
        <div>
          <h1>Checklist models</h1>
          <h2>
            <CourseTitle />
          </h2>
        </div>
        {canManage && (
          <>
            <Button
              text="Delete"
              disabled={selectedModels.length === 0}
              defaultStyle={`primary${selectedModels.length === 0 ? ' disabled' : ''}`}
              onClick={() => setConfirmDeletePopup(true)}
            />
            <Button
              text={generating ? 'Generating…' : 'Generate / Update checklists'}
              disabled={generating}
              defaultStyle={`primary${generating ? ' disabled' : ''}`}
              onClick={onGenerate}
            />
            <Button text="Add a checklist model" defaultStyle="primary" onClick={openModalAdd} />
          </>
        )}
      </div>
      <Table
        selectedRows={selection => setSelectedModels(selection)}
        datas={data.datas}
        headers={data.headers}
        editable={canManage}
        onEdit={openModalEdit}
        extraActions={
          canManage
            ? [
                {
                  title: 'Apply to all active flights',
                  icon: <PlaneTakeOffIcon width={22} height={22} />,
                  onClick: row => setApplyCandidate(row),
                },
                {
                  title: 'Delete this checklist model',
                  icon: <DeleteIcon width={18} height={18} color="#c62828" fill="#c62828" />,
                  onClick: row => setDeleteCandidate(row),
                },
              ]
            : []
        }
        name="checklistModelsFilters"
      />
      {modalVisibility && (
        <Modal onClose={() => setModalVisibility(false)}>
          <FormChecklistModel onSubmit={onSubmitForm} editId={editId} type={formType} />
        </Modal>
      )}
      {confirmDeletePopup && (
        <ConfirmDelete
          onClose={() => setConfirmDeletePopup(false)}
          list={selectedModels}
          onConfirm={onDelete}
          formatList={model => `Checklist model : ${model.name}`}
        />
      )}
      {applyCandidate && (
        <Modal onClose={() => !applying && setApplyCandidate(null)}>
          <div className="modal__content">
            <h2>Apply to all active flights</h2>
            <p className="checklist-apply__warning">
              ⚠ You are about to modify <strong>all the active flights</strong> of the course:
              model <strong>{applyCandidate.name}</strong> will be added to each of them.
            </p>
            <p>
              Models already checked on a flight are kept (nothing is removed). The checklists
              themselves are created by “Generate / Update checklists”.
            </p>
            <div className="m-auto p-20">
              <Button
                text={applying ? 'Applying…' : 'APPLY TO ALL FLIGHTS'}
                disabled={applying}
                defaultStyle={`primary${applying ? ' disabled' : ''}`}
                onClick={onApplyToActiveFlights}
              />
            </div>
          </div>
        </Modal>
      )}
      {deleteCandidate && (
        <ConfirmDelete
          onClose={() => setDeleteCandidate(null)}
          list={[deleteCandidate]}
          onConfirm={onDeleteOne}
          formatList={model =>
            `Checklist model : ${model.name} — already generated checklists are kept (unchanged)`
          }
        />
      )}
    </div>
  );
};

export default ChecklistModels;
